import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Tooltip, { TooltipTrigger } from '@/components/atoms/Tooltip';
import type { BioscopeDataQuality } from '@/domain/feedingDashboard';

import type { PelletDataQuality } from '../bioscopeQuality';

import styles from './BioscopeQualityDot.module.scss';

/**
 * What a dot rates. A Bioscope dot rates the weight estimates. A pellet dot rates the
 * feeding camera's pellet data, which a pen can have while its estimates are still
 * collecting, so it has its own wording: the Bioscope "ready" text would call those
 * estimates reliable.
 */
export type QualityDotRating =
    | { quality: BioscopeDataQuality; qualitySubject?: 'bioscope' }
    | { quality: PelletDataQuality; qualitySubject: 'pellet' };

/** A panel's or card's rating, where a panel with no Bioscope or pellet data has none. */
export type SurfaceQualityRating =
    | { quality?: BioscopeDataQuality; qualitySubject?: 'bioscope' }
    | { quality: PelletDataQuality; qualitySubject: 'pellet' };

interface DotButtonProps {
    label: string;
    quality: BioscopeDataQuality | PelletDataQuality;
}

/** The dot as a tooltip trigger, so a touch or keyboard user can open its label too. */
function DotButton(props: DotButtonProps) {
    const { label, quality } = props;
    return (
        <TooltipTrigger
            className={classNames(styles.target, styles[quality])}
            label={label}
            data-quality={quality}>
            <span className={styles.dot} aria-hidden="true" />
        </TooltipTrigger>
    );
}

/**
 * Data-quality indicator required on every Bioscope-based KPI card and graph
 * (IPS req 11): green once enough measurement days exist for reliable estimates,
 * yellow during the startup period, grey when the selected period holds no Bioscope
 * data at all. The grey state matters: without it a mature pen viewed over an empty
 * period shows green beside a row of dashes, which reads as "these blanks are
 * reliable".
 *
 * Shared rather than duplicated per surface so the two colours cannot drift apart,
 * and so "which surfaces have an indicator" is answerable by finding the callers.
 */
export function BioscopeQualityDot(props: QualityDotRating) {
    const { quality } = props;
    const { t } = useTranslation();
    // The dot is the only carrier of this state on a chart, so it needs an
    // accessible label rather than colour alone.
    const label =
        props.qualitySubject === 'pellet'
            ? t(`feedingModule.quality.pellet.${props.quality}`)
            : t(`feedingModule.quality.${quality}`);

    // The shared tooltip portals the bubble into the body, so a card or a chart header neither
    // clips it nor grows around it.
    return (
        <Tooltip content={label} placement="top" layout="fit" targetClassName={styles.anchor}>
            <DotButton label={label} quality={quality} />
        </Tooltip>
    );
}

/** The dot a panel or card shows for its rating, or nothing when it has none. */
export function SurfaceQualityDot(props: { rating: SurfaceQualityRating }) {
    const { rating } = props;
    if (rating.qualitySubject === 'pellet') {
        return <BioscopeQualityDot quality={rating.quality} qualitySubject="pellet" />;
    }
    return rating.quality ? <BioscopeQualityDot quality={rating.quality} /> : null;
}
